<script setup lang="ts">
import { ref, computed, watch } from 'vue'
import { validateEmail, validatePasswordStrength, checkEmail, checkUsername, checkNickname, getPasskeyAuthOptions, verifyPasskeyAuth, loginWithRecoveryCode } from '../services/authService'
import { startAuthentication } from '@simplewebauthn/browser'
import { useUiStore } from '../stores/uiStore'
import { useUserStore } from '../stores/userStore'

interface Props {
  isOpen: boolean
}

interface Emits {
  (e: 'close'): void
  (e: 'login-success'): void
  (e: 'forgot-password'): void
}

defineProps<Props>()
const emit = defineEmits<Emits>()

const uiStore = useUiStore()
const userStore = useUserStore()

const activeTab = ref<'login' | 'register'>('login')
const isLoading = ref(false)
const isPasskeyLoading = ref(false)
const passkeyError = ref('')

// 恢復碼登入
const isShowingRecoveryCodeInput = ref(false)
const recoveryCode = ref('')
const isRecoveryCodeLoading = ref(false)
const recoveryCodeError = ref('')

// 登入表單
const loginForm = ref({
  credential: '',
  password: '',
})
const credentialError = ref('')
const passwordError = ref('')
const loginError = ref('')

// 註冊表單
const registerForm = ref({
  email: '',
  username: '',
  nickname: '',
  password: '',
  confirmPassword: '',
})
const registerErrors = ref({
  email: '',
  username: '',
  nickname: '',
  password: '',
  confirmPassword: '',
})
const registerSuccess = ref('')

// 追蹤使用者帳號和暱稱是否被手動修改（v0.3.37）
const usernameManuallyModified = ref(false)
const nicknameManuallyModified = ref(false)

// 監聽 email 變化，自動填入 username 和 nickname 的前置字串（v0.3.37）
watch(
  () => registerForm.value.email,
  (newEmail) => {
    if (newEmail && newEmail.includes('@')) {
      const prefix = newEmail.split('@')[0]
      if (!usernameManuallyModified.value) {
        registerForm.value.username = prefix
      }
      if (!nicknameManuallyModified.value) {
        registerForm.value.nickname = prefix
      }
    }
  }
)

const handleLogin = async () => {
  credentialError.value = ''
  passwordError.value = ''

  if (!loginForm.value.credential) {
    credentialError.value = '此欄不可留白'
    return
  }
  if (!loginForm.value.password) {
    passwordError.value = '此欄不可留白'
    return
  }

  isLoading.value = true
  const result = await userStore.login(loginForm.value.credential, loginForm.value.password)

  if (result.success) {
    resetLoginForm()
    emit('login-success')
    emit('close')
  } else {
    // 根據錯誤訊息設定到對應欄位（v0.3.38）
    if (result.error?.includes('用戶不存在')) {
      credentialError.value = '此帳號不存在'
    } else if (result.error?.includes('密碼')) {
      passwordError.value = '此密碼錯誤'
    } else {
      credentialError.value = result.error || '登入失敗'
    }
  }

  isLoading.value = false
}

const resetLoginForm = () => {
  loginForm.value = {
    credential: '',
    password: '',
  }
  credentialError.value = ''
  passwordError.value = ''
}

const handleLoginCancel = () => {
  resetLoginForm()
  emit('close')
}

const resetRegisterForm = () => {
  registerForm.value = {
    email: '',
    username: '',
    nickname: '',
    password: '',
    confirmPassword: '',
  }
  registerErrors.value = {
    email: '',
    username: '',
    nickname: '',
    password: '',
    confirmPassword: '',
  }
  usernameManuallyModified.value = false
  nicknameManuallyModified.value = false
  registerSuccess.value = ''
}

const handleRegister = async () => {
  registerErrors.value = {
    email: '',
    username: '',
    nickname: '',
    password: '',
    confirmPassword: '',
  }
  registerSuccess.value = ''

  // 驗證所有欄位
  let hasError = false

  if (!registerForm.value.email) {
    registerErrors.value.email = '此欄不可留白'
    hasError = true
  } else if (!validateEmail(registerForm.value.email)) {
    registerErrors.value.email = '請輸入有效的電子信箱'
    hasError = true
  }

  if (!registerForm.value.username) {
    registerErrors.value.username = '此欄不可留白'
    hasError = true
  }

  if (!registerForm.value.nickname) {
    registerErrors.value.nickname = '此欄不可留白'
    hasError = true
  }

  if (!registerForm.value.password) {
    registerErrors.value.password = '此欄不可留白'
    hasError = true
  } else {
    const validation = validatePasswordStrength(registerForm.value.password)
    if (!validation.valid) {
      registerErrors.value.password = validation.error || '密碼強度不足'
      hasError = true
    }
  }

  if (!registerForm.value.confirmPassword) {
    registerErrors.value.confirmPassword = '此欄不可留白'
    hasError = true
  } else if (registerForm.value.password !== registerForm.value.confirmPassword) {
    registerErrors.value.confirmPassword = '密碼不符'
    hasError = true
  }

  if (hasError) return

  isLoading.value = true

  // 檢查重複帳號（v0.3.37）
  const [emailCheck, usernameCheck, nicknameCheck] = await Promise.all([
    checkEmail(registerForm.value.email),
    checkUsername(registerForm.value.username),
    checkNickname(registerForm.value.nickname),
  ])

  if (emailCheck.exists) {
    registerErrors.value.email = '此電子信箱已存在'
  }
  if (usernameCheck.exists) {
    registerErrors.value.username = '此帳號已存在'
  }
  if (nicknameCheck.exists) {
    registerErrors.value.nickname = '此暱稱已存在'
  }

  if (emailCheck.exists || usernameCheck.exists || nicknameCheck.exists) {
    isLoading.value = false
    return
  }

  const result = await userStore.register(
    registerForm.value.email,
    registerForm.value.username,
    registerForm.value.nickname,
    registerForm.value.password
  )

  if (result.success) {
    registerSuccess.value = '註冊成功！請使用新帳號登入'
    resetRegisterForm()
    activeTab.value = 'login'
  } else {
    // Fallback：若 API 返回錯誤，解析錯誤訊息到對應欄位
    if (result.error?.includes('Email')) {
      registerErrors.value.email = result.error
    } else if (result.error?.includes('Username')) {
      registerErrors.value.username = result.error
    } else if (result.error?.includes('Nickname')) {
      registerErrors.value.nickname = result.error
    } else {
      registerErrors.value.email = result.error || '註冊失敗'
    }
  }

  isLoading.value = false
}

const handleRegisterCancel = () => {
  resetRegisterForm()
  emit('close')
}

const handleOverlayClick = () => {
  // 在 login 和 register tab 時都不關閉對話框（v0.3.38）
  if (activeTab.value === 'login' || activeTab.value === 'register') return
  emit('close')
}

const errorFontSize = computed(() => {
  return Math.max(8, Math.floor(uiStore.fontSize / 2))
})

const isWebAuthnSupported = computed(() => {
  return typeof window !== 'undefined' && !!window.PublicKeyCredential
})

const handlePasskeyLogin = async () => {
  passkeyError.value = ''
  isPasskeyLoading.value = true

  try {
    const optionsResponse = await getPasskeyAuthOptions()
    if (!optionsResponse.success || !optionsResponse.data) {
      passkeyError.value = optionsResponse.error || 'Failed to get Passkey options'
      isPasskeyLoading.value = false
      return
    }

    const { options, challengeId } = optionsResponse.data

    const assertion = await startAuthentication({
      optionsJSON: options,
    })

    const verifyResponse = await verifyPasskeyAuth(challengeId, assertion)
    if (verifyResponse.success && verifyResponse.data) {
      userStore.setSession(verifyResponse.data.user, verifyResponse.data.csrfToken)
      emit('login-success')
      emit('close')
    } else {
      passkeyError.value = verifyResponse.error || 'Passkey 驗證失敗'
    }
  } catch (error: any) {
    console.error('Passkey login error:', error)
    passkeyError.value = error.message || 'Passkey 驗證失敗'
  } finally {
    isPasskeyLoading.value = false
  }
}

const handleRecoveryCodeLogin = async () => {
  recoveryCodeError.value = ''

  if (!recoveryCode.value.trim()) {
    recoveryCodeError.value = '請輸入恢復碼'
    return
  }

  isRecoveryCodeLoading.value = true

  try {
    const result = await loginWithRecoveryCode(recoveryCode.value.trim())
    if (result.success && result.data) {
      userStore.setSession(result.data.user, result.data.csrfToken)
      recoveryCode.value = ''
      isShowingRecoveryCodeInput.value = false
      emit('login-success')
      emit('close')
    } else {
      recoveryCodeError.value = result.error || '恢復碼無效或已使用'
    }
  } catch (error: any) {
    console.error('Recovery code login error:', error)
    recoveryCodeError.value = error.message || '登入失敗'
  } finally {
    isRecoveryCodeLoading.value = false
  }
}
</script>

<template>
  <div v-if="isOpen" class="auth-dialog-overlay" @click="handleOverlayClick">
    <div class="auth-dialog" @click.stop>
      <!-- 分頁標籤 -->
      <div class="auth-tabs">
        <button :class="{ active: activeTab === 'login' }" @click="activeTab = 'login'">
          登入
        </button>
        <button :class="{ active: activeTab === 'register' }" @click="activeTab = 'register'">
          註冊
        </button>
      </div>

      <!-- 登入分頁 -->
      <div v-if="activeTab === 'login'" class="auth-form">
        <!-- Passkey 登入 -->
        <div v-if="isWebAuthnSupported" class="passkey-section">
          <button
            class="passkey-btn passkey-btn-primary"
            @click="handlePasskeyLogin"
            :disabled="isPasskeyLoading"
            :style="{ fontSize: uiStore.fontSize + 'px' }"
          >
            <span class="passkey-icon">📱</span>
            <span>{{ isPasskeyLoading ? '驗證中...' : '使用手機 Passkey 登入（推薦）' }}</span>
          </button>
          <div class="passkey-subtitle">用手機掃描 QR 碼，使用 Face ID 或指紋登入</div>
          <div v-if="passkeyError" class="error-text" :style="{ fontSize: errorFontSize + 'px' }">
            {{ passkeyError }}
          </div>
          <div class="divider-or">
            <span>或使用其他方式</span>
          </div>
        </div>

        <div class="form-group">
          <label for="login-credential">帳號</label>
          <input
            id="login-credential"
            v-model="loginForm.credential"
            type="text"
            placeholder="電子信箱／帳號／暱稱"
            class="form-input"
            :disabled="isLoading"
          />
          <div v-if="credentialError" class="error-text" :style="{ fontSize: errorFontSize + 'px' }">
            {{ credentialError }}
          </div>
        </div>

        <div class="form-group">
          <label for="login-password">密碼</label>
          <input
            id="login-password"
            v-model="loginForm.password"
            type="password"
            placeholder="密碼"
            class="form-input"
            :disabled="isLoading"
            @keyup.enter="handleLogin"
          />
          <div v-if="passwordError" class="error-text" :style="{ fontSize: errorFontSize + 'px' }">
            {{ passwordError }}
          </div>
        </div>

        <div class="forgot-password-wrap">
          <a href="#" @click.prevent="emit('forgot-password')"
             :style="{ fontSize: errorFontSize + 'px' }">忘記密碼？</a>
          <span class="separator" :style="{ fontSize: errorFontSize + 'px' }">｜</span>
          <a href="#" @click.prevent="isShowingRecoveryCodeInput = !isShowingRecoveryCodeInput"
             :style="{ fontSize: errorFontSize + 'px' }">使用恢復碼</a>
        </div>

        <!-- 恢復碼輸入區塊 -->
        <div v-if="isShowingRecoveryCodeInput" class="recovery-code-section">
          <div class="form-group">
            <label for="recovery-code">恢復碼</label>
            <input
              id="recovery-code"
              v-model="recoveryCode"
              type="text"
              placeholder="輸入恢復碼 (如: XXXXX-XXXXX)"
              class="form-input"
              :disabled="isRecoveryCodeLoading"
              @keyup.enter="handleRecoveryCodeLogin"
            />
            <div v-if="recoveryCodeError" class="error-text" :style="{ fontSize: errorFontSize + 'px' }">
              {{ recoveryCodeError }}
            </div>
          </div>
          <button
            class="auth-btn recovery-code-btn"
            @click="handleRecoveryCodeLogin"
            :disabled="isRecoveryCodeLoading"
            :style="{ fontSize: uiStore.fontSize + 'px' }"
          >
            {{ isRecoveryCodeLoading ? '驗證中...' : '確認登入' }}
          </button>
        </div>

        <div class="button-group">
          <button
            class="auth-btn"
            @click="handleLogin"
            :disabled="isLoading"
            :style="{ fontSize: uiStore.fontSize + 'px' }"
          >
            {{ isLoading ? '登入中...' : '登入' }}
          </button>
          <button
            class="auth-btn cancel-btn"
            @click="handleLoginCancel"
            :disabled="isLoading"
            :style="{ fontSize: uiStore.fontSize + 'px' }"
          >
            取消
          </button>
        </div>
      </div>

      <!-- 註冊分頁 -->
      <div v-if="activeTab === 'register'" class="auth-form">
        <div class="form-group">
          <label for="register-email">電子信箱</label>
          <input
            id="register-email"
            v-model="registerForm.email"
            type="email"
            placeholder="電子信箱"
            class="form-input"
            :disabled="isLoading"
          />
          <div v-if="registerErrors.email" class="error-text" :style="{ fontSize: errorFontSize + 'px' }">
            {{ registerErrors.email }}
          </div>
        </div>

        <div class="form-group">
          <label for="register-username">使用者帳號</label>
          <input
            id="register-username"
            v-model="registerForm.username"
            type="text"
            placeholder="使用者帳號"
            class="form-input"
            :disabled="isLoading"
            @input="usernameManuallyModified = true"
          />
          <div v-if="registerErrors.username" class="error-text" :style="{ fontSize: errorFontSize + 'px' }">
            {{ registerErrors.username }}
          </div>
        </div>

        <div class="form-group">
          <label for="register-nickname">暱稱</label>
          <input
            id="register-nickname"
            v-model="registerForm.nickname"
            type="text"
            placeholder="暱稱"
            class="form-input"
            :disabled="isLoading"
            @input="nicknameManuallyModified = true"
          />
          <div v-if="registerErrors.nickname" class="error-text" :style="{ fontSize: errorFontSize + 'px' }">
            {{ registerErrors.nickname }}
          </div>
        </div>

        <div class="form-group">
          <label for="register-password">密碼</label>
          <input
            id="register-password"
            v-model="registerForm.password"
            type="password"
            placeholder="密碼"
            class="form-input"
            :disabled="isLoading"
          />
          <div v-if="registerErrors.password" class="error-text" :style="{ fontSize: errorFontSize + 'px' }">
            {{ registerErrors.password }}
          </div>
        </div>

        <div class="form-group">
          <label for="register-confirm-password">確認密碼</label>
          <input
            id="register-confirm-password"
            v-model="registerForm.confirmPassword"
            type="password"
            placeholder="確認密碼"
            class="form-input"
            :disabled="isLoading"
          />
          <div v-if="registerErrors.confirmPassword" class="error-text" :style="{ fontSize: errorFontSize + 'px' }">
            {{ registerErrors.confirmPassword }}
          </div>
        </div>

        <div v-if="registerSuccess" class="success-text" :style="{ fontSize: errorFontSize + 'px' }">
          {{ registerSuccess }}
        </div>

        <div class="button-group">
          <button
            class="auth-btn"
            @click="handleRegister"
            :disabled="isLoading"
            :style="{ fontSize: uiStore.fontSize + 'px' }"
          >
            {{ isLoading ? '註冊中...' : '註冊' }}
          </button>
          <button
            class="auth-btn cancel-btn"
            @click="handleRegisterCancel"
            :disabled="isLoading"
            :style="{ fontSize: uiStore.fontSize + 'px' }"
          >
            取消
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
.auth-dialog-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 300;
}

.auth-dialog {
  background: white;
  border-radius: 8px;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.2);
  width: 90%;
  max-width: 400px;
  overflow: hidden;
}

.auth-tabs {
  display: flex;
  border-bottom: 1px solid #e5e7eb;
}

.auth-tabs button {
  flex: 1;
  padding: 12px;
  background: white;
  border: none;
  cursor: pointer;
  font-weight: 500;
  color: #6b7280;
  transition: all 0.2s;
}

.auth-tabs button.active {
  color: #3b82f6;
  border-bottom: 2px solid #3b82f6;
  margin-bottom: -1px;
}

.auth-tabs button:hover:not(.active) {
  background: #f9fafb;
}

.auth-form {
  padding: 24px;
}

.form-group {
  margin-bottom: 16px;
}

.form-group label {
  display: block;
  font-weight: 500;
  color: #374151;
  font-size: 14px;
  margin-bottom: 4px;
}

.form-input {
  width: 100%;
  padding: 10px 12px;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  font-size: 14px;
  box-sizing: border-box;
}

.form-input:focus {
  outline: none;
  border-color: #3b82f6;
  box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
}

.form-input:disabled {
  background: #f3f4f6;
  cursor: not-allowed;
}

.error-text {
  color: #ef4444;
  margin-top: 4px;
  font-size: 12px;
}

.forgot-password-wrap {
  margin-bottom: 16px;
  display: flex;
  align-items: center;
  gap: 6px;
}

.forgot-password-wrap a {
  color: #3b82f6;
  text-decoration: underline;
  cursor: pointer;
}

.separator {
  color: #d1d5db;
}

.error-block {
  margin-bottom: 16px;
  padding: 8px;
  background: #fee2e2;
  border-radius: 4px;
}

.success-text {
  color: #10b981;
  margin-bottom: 16px;
  padding: 8px;
  background: #ecfdf5;
  border-radius: 4px;
}

.button-group {
  display: flex;
  gap: 8px;
}

.auth-btn {
  flex: 1;
  padding: 10px;
  background: #3b82f6;
  color: white;
  border: none;
  border-radius: 6px;
  cursor: pointer;
  font-weight: 500;
  transition: background 0.2s;
}

.auth-btn:hover:not(:disabled) {
  background: #2563eb;
}

.auth-btn:disabled {
  background: #9ca3af;
  cursor: not-allowed;
}

.auth-btn.cancel-btn {
  background: #9ca3af;
}

.auth-btn.cancel-btn:hover:not(:disabled) {
  background: #6b7280;
}

.passkey-section {
  margin-bottom: 16px;
}

.passkey-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  width: 100%;
  padding: 12px;
  background: #3b82f6;
  color: white;
  border: none;
  border-radius: 6px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
  white-space: nowrap;
}

.passkey-btn-primary {
  padding: 14px;
  font-size: 1.05em;
  box-shadow: 0 4px 12px rgba(59, 130, 246, 0.3);
}

.passkey-btn:hover:not(:disabled) {
  background: #2563eb;
  transform: translateY(-2px);
  box-shadow: 0 6px 16px rgba(59, 130, 246, 0.4);
}

.passkey-btn:disabled {
  background: #9ca3af;
  cursor: not-allowed;
  transform: none;
}

.passkey-subtitle {
  margin-top: 8px;
  font-size: 12px;
  color: #6b7280;
  text-align: center;
}

.passkey-icon {
  font-size: 1.2em;
}

.divider-or {
  display: flex;
  align-items: center;
  margin: 12px 0;
  color: #6b7280;
  font-size: 12px;
}

.divider-or::before,
.divider-or::after {
  content: '';
  flex: 1;
  height: 1px;
  background: #d1d5db;
}

.divider-or::before {
  margin-right: 8px;
}

.divider-or::after {
  margin-left: 8px;
}

.recovery-code-section {
  margin-bottom: 16px;
  padding: 12px;
  background: #f9fafb;
  border-radius: 6px;
  border: 1px solid #e5e7eb;
}

.recovery-code-btn {
  width: 100%;
  padding: 10px;
}
</style>
